import { ChangeEventHandler, ReactNode, useState } from "react";
import clsx from "clsx";
// Components
import LoginContainer from "@/Auth/Layouts/LoginContainer";
import StartAdornment from "@Auth/components/StartAdornment";
import EndAdornment from "@Auth/components/EndAdornment";
import { PasswordValidationHints } from "@Auth/components/PasswordValidationHints";
// Helpers
import { checkPasswordStrength, iconFromStrength } from "@/Auth/helpers/validators";
// MUI
import { Button, Stack, Typography, TextField, CircularProgress } from "@mui/material";
// Styles
import "@/Auth/styles/login.css";
// Icons
import Hidden from "@/Auth/assets/icons/hidden.svg";
import Lock from "@/Auth/assets/icons/lock.svg";
import Visible from "@/Auth/assets/icons/visible.svg";
import CheckBadge from "@/Auth/assets/icons/checkbadge.svg";
import { ResetPasswordProps } from "@/Auth/types";
import { CLASSNAME_TEXTFIELD } from "@/Auth/constants";

export default function ResetPassword({ state, updateState, ...props }: Readonly<ResetPasswordProps>) {
	const buttonText = props.buttonText ?? props.title;
	const [strengthIcon, setStrengthIcon] = useState<ReactNode>(null);
	const [strengthText, setStrengthText] = useState<string>("");

	const handleChangePassword: ChangeEventHandler<HTMLInputElement> = (e) => {
		if (e.target.value === "") {
			setStrengthIcon(null);
			setStrengthText("");
			updateState({
				...state,
				password: e.target.value,
			});
		} else {
			const strength = checkPasswordStrength(e.target.value);
			setStrengthIcon(iconFromStrength(strength));
			setStrengthText(strength);
			updateState({
				...state,
				validForm: strength === "Strong",
				password: e.target.value,
			});
		}
	};

	const handleChangePasswordConfirm: ChangeEventHandler<HTMLInputElement> = (e) => {
		if (e.target.value === "") {
			updateState({
				...state,
				validForm: false,
				passwordConfirm: e.target.value,
			});
		} else {
			const strength = checkPasswordStrength(e.target.value);
			updateState({
				...state,
				validForm: strength === "Strong" && e.target.value === state.password,
				passwordConfirm: e.target.value,
			});
		}
	};

	const handleClickShowPassword = () => {
		updateState({
			...state,
			showPassword: !state.showPassword,
		});
	};

	const handleClickShowPasswordConfirm = () => {
		updateState({
			...state,
			showPasswordConfirm: !state.showPasswordConfirm,
		});
	};

	const passwordsMatch = state.password === state.passwordConfirm;
	const isSubmitable = state.validForm && !state.isLoading && passwordsMatch;

	return (
		<LoginContainer>
			<form
				className={clsx({
					"form-login form-custom": true,
					"form-error": state.error,
				})}
				noValidate
				onSubmit={props.handleSubmit}
			>
				<Stack direction="column" className="mx-[13px]">
					<Typography className="text-title mb-[36px] mt-0 text-center">{props.title}</Typography>

					<TextField
						placeholder="New Password"
						focused={true}
						fullWidth
						className="mb-[32px] mt-0"
						required={true}
						type={state.showPassword ? "text" : "password"}
						value={state.password}
						onChange={handleChangePassword}
						InputProps={{
							className: CLASSNAME_TEXTFIELD,
							startAdornment: <StartAdornment src={Lock} alt="Lock Icon" />,
							endAdornment: (
								<EndAdornment
									handleOnClick={handleClickShowPassword}
									strength={{ text: strengthText, icon: strengthIcon }}
									iconHidden={Hidden}
									iconVisible={Visible}
									showPassword={state.showPassword}
								/>
							),
						}}
					/>

					<TextField
						placeholder="Confirm New Password"
						error={state.password !== state.passwordConfirm}
						fullWidth
						className="mb-[20px] mt-0"
						required={true}
						type={state.showPasswordConfirm ? "text" : "password"}
						value={state.passwordConfirm}
						onChange={handleChangePasswordConfirm}
						InputProps={{
							className: CLASSNAME_TEXTFIELD,
							startAdornment: <StartAdornment src={CheckBadge} alt="Check Badge Icon" />,
							endAdornment: (
								<EndAdornment
									handleOnClick={handleClickShowPasswordConfirm}
									iconHidden={Hidden}
									iconVisible={Visible}
									showPassword={state.showPasswordConfirm}
								/>
							),
						}}
					/>

					<Stack>
						<PasswordValidationHints password={state.password} />
					</Stack>
				</Stack>

				<Stack className="p-0 min-h-[40px] mx-[13px]">
					<Typography
						className={clsx({
							"alert-message": true,
							block: state.error || !passwordsMatch,
							hidden: !(state.error || !passwordsMatch),
						})}
					>
						{!passwordsMatch ? "Passwords must match" : state.message}
					</Typography>
				</Stack>

				<Stack direction="row" justifyContent="center" alignContent="center" className="mx-[13px] mt-[5px]">
					<Button
						variant="contained"
						className="btn-submit"
						type="submit"
						disableElevation
						disabled={!isSubmitable}
					>
						{state.isLoading ? <CircularProgress color="inherit" size={25} /> : buttonText}
					</Button>
				</Stack>
			</form>
		</LoginContainer>
	);
}
